﻿import "primeflex/primeflex.min.css";
import 'primeicons/primeicons.css'
import "@fortawesome/fontawesome-free/css/all.min.css";
//import 'primevue/resources/themes/lara-dark-blue/theme.css'
import { createApp } from 'vue'
import App from './App.vue'
import { createLogger } from 'vue-logger-plugin'
import router from './router.ts'
import utility from './utils/utility';
import { piniaStore } from './pinia'
import i18n from './locales'; // 假設你把 i18n 設定放在這裡
import PrimeVue from 'primevue/config';
import { definePreset, palette } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import ConfirmationService from 'primevue/confirmationservice';
import DialogService from 'primevue/dialogservice'
import ToastService from 'primevue/toastservice';
import InputTextEx from './components/InputTextEx.vue'
import { Z_INDEX } from '@/constants/ui';

const app = createApp(App)

app.use(i18n);
const currentLocale = i18n.global.locale.value;
const primevueMessages = (i18n.global.getLocaleMessage(currentLocale) as any).primevue;

//vue-logger-plugin
const isProduction = import.meta.env.MODE === 'production';

// log level 初值優先序：localStorage 覆寫 > 編譯期預設。
// （後端 Bootstrap logLevel 於 App.vue initialize 後才套用，且僅在無 localStorage 覆寫時生效。）
const VALID_LOG_LEVELS = ['debug', 'info', 'warn', 'error'];   // 對齊 utility.ts class log.levels
const resolveInitialLogLevel = (): 'debug' | 'info' | 'warn' | 'error' => {
    // 支援 URL query 臨時調整（供支援人員，不需開 devtools）：
    //   ?loglevel=debug → 寫入覆寫；?loglevel=clear → 清除覆寫
    const q = new URLSearchParams(window.location.search).get('loglevel');
    if (q === 'clear') {
        localStorage.removeItem('logLevel');
    } else if (q && VALID_LOG_LEVELS.includes(q)) {
        localStorage.setItem('logLevel', q);
    }
    // localStorage 覆寫（優先序最高）
    const override = localStorage.getItem('logLevel');
    if (override && VALID_LOG_LEVELS.includes(override)) {
        return override as 'debug' | 'info' | 'warn' | 'error';
    }
    // 編譯期預設（prod=info、dev=debug）
    return isProduction ? 'info' : 'debug';
};

const logger = createLogger({
    enabled: true,
    consoleEnabled: true,
    //(error/warn/info/debug)
    level: resolveInitialLogLevel(),
});
app.use(logger);

//Vue Router 4
app.use(router)

//Pinia
app.use(piniaStore);

//使用util$
app.use(utility, { router, logger });

// 官方 Noir 變體（建立於 Aura 之上）：以灰階(zinc)當 primary 主色 → light 近黑、dark 近白（主色無彩色）。
// surface：light=slate、dark=zinc（即 Aura 原廠預設 surface）。
// 註：palette() 回傳型別為 string | ColorScale，展開(...)需 as Record<string,string> 斷言為物件，否則報 TS2698。
const MppPreset = definePreset(Aura, {
    components: {
        toast: {
            summary: {
                //--p-toast-summary-font-weight
                fontWeight: '900'
            },
            detail: {
                //--p-toast-detail-font-weight
                fontWeight: '900'
            },
            root: {
                width: 'auto',
                //--p-toast-border-width
                borderWidth: '1px 1px 1px 6px'
            },
            colorScheme: {
                light: {
                    error: {
                        //--p-toast-error-background
                        background: 'var(--p-pink-100)',
                    },
                    warn: {
                        //--p-toast-warn-background
                        background: 'var(--p-pink-100)',
                    },
                    success: {
                        //--p-toast-success-background
                        background: 'var(--p-pink-100)',
                    },
                    info: {
                        //--p-toast-info-background
                        background: 'var(--p-pink-100)',
                    },
                    secondary: {
                        //--p-toast-secondary-background
                        background: 'var(--p-pink-100)',
                    },
                    contrast: {
                        //--p-toast-contrast-background
                        background: 'var(--p-pink-100)',
                    },
                },
                dark: {
                    error: {
                        //--p-toast-error-background
                        background: 'var(--p-pink-100)',
                        detailColor: 'var(--p-slate-900)'
                    },
                    warn: {
                        //--p-toast-warn-background
                        background: 'var(--p-pink-100)',
                        detailColor: 'var(--p-slate-900)'
                    },
                    success: {
                        //--p-toast-success-background
                        background: 'var(--p-pink-100)',
                        detailColor: 'var(--p-slate-900)'
                    },
                    info: {
                        //--p-toast-info-background
                        background: 'var(--p-pink-100)',
                        detailColor: 'var(--p-slate-900)'
                    },
                    secondary: {
                        //--p-toast-secondary-background
                        background: 'var(--p-pink-100)',
                    },
                    contrast: {
                        //--p-toast-contrast-background
                        background: 'var(--p-pink-100)',
                        detailColor: 'var(--p-slate-900)'
                    },
                }
            }
        },
        message: {
            colorScheme: {
                light: {
                    //--p-message-info-color
                    info: {
                        color: 'var(--p-blue-800)'
                    },
                    //--p-message-success-color
                    success: {
                        color: 'var(--p-green-800)'
                    },
                    //--p-message-warn-color
                    warn: {
                        color: 'var(--p-yellow-800)'
                    },
                    //--p-message-error-color
                    error: {
                        color: 'var(--p-red-700)'
                    }
                },
                dark: {
                    //--p-message-info-color
                    info: {
                        color: 'var(--p-blue-800)'
                    },
                    //--p-message-success-color
                    success: {
                        color: 'var(--p-green-800)'
                    },
                    //--p-message-warn-color
                    warn: {
                        color: 'var(--p-yellow-800)'
                    },
                    //--p-message-error-color
                    error: {
                        color: 'var(--p-red-700)'
                    }
                }
            }
        }
    },
    semantic: {
        primary: palette('{zinc}'),
        colorScheme: {
            light: {
                primary: {
                    color: '{zinc.950}',
                    inverseColor: '#ffffff',
                    hoverColor: '{zinc.900}',
                    activeColor: '{zinc.800}'
                },
                highlight: {
                    background: '{zinc.950}',
                    focusBackground: '{zinc.700}',
                    color: '#ffffff',
                    focusColor: '#ffffff'
                },
                surface: { 0: '#ffffff', ...(palette('{slate}') as Record<string, string>) }
            },
            dark: {
                primary: {
                    color: '{zinc.50}',
                    inverseColor: '{zinc.950}',
                    hoverColor: '{zinc.100}',
                    activeColor: '{zinc.200}'
                },
                highlight: {
                    background: 'rgba(250, 250, 250, .16)',
                    focusBackground: 'rgba(250, 250, 250, .24)',
                    color: 'rgba(255,255,255,.87)',
                    focusColor: 'rgba(255,255,255,.87)'
                },
                surface: { 0: '#ffffff', ...(palette('{zinc}') as Record<string, string>) }
            }
        }
    }
});

//PrimeVue
app.use(PrimeVue, {
    ripple: true,
    inputStyle: 'filled',
    // Default theme configuration
    theme: {
        preset: MppPreset,
        options: {
            prefix: 'p',
            darkModeSelector: '.mpp-app-dark',
            cssLayer: false
        }
    },
    locale: {
        // 先定義基礎結構，防止組件因為找不到 aria 物件而崩潰
        //aria: {
        //    scrollTop: 'Scroll Top',
        //    nextPageLabel: 'Next Page',
        //    prevPageLabel: 'Previous Page',
        //    close: 'Close',
        //    // ... 預防 primevue.ts 漏寫
        //},
        // 確保 aria 至少是一個空物件，這樣讀取 aria.xxx 時只會得到 undefined 而不會 crash
        aria: {},
        // 用 i18n 裡的翻譯覆蓋掉上面的預設值
        ...primevueMessages,
        // 注意：如果 primevueMessages 裡也有 aria，
        // 展開運算子會用 primevueMessages.aria 完全取代掉上面的 aria。
        // 如果想更保險，可以寫成：
        // aria: {
        //    scrollTop: 'Scroll Top',
        //    ...primevueMessages?.aria
        // }
    },
    zIndex: {
        modal: Z_INDEX.DIALOG,      // 1100
        overlay: Z_INDEX.DROPDOWN,  // 1000
        menu: Z_INDEX.MENU,         // 1000
        tooltip: Z_INDEX.TOOLTIP,   // 1110
        toast: Z_INDEX.TOAST        // 1200
    }
})

app.use(ToastService);
app.use(ConfirmationService)
app.use(DialogService);

app.component("InputTextEx", InputTextEx);

app.mount('#app')